'use client';

import type { MouseEvent, PropsWithChildren} from 'react';
import { Fragment, useCallback, useRef } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { nextProjectUrl } from '@shared/constants/urls';

import { Activity, type ActivityHandle } from '@ui/uikit/components/common/Activity';
import { Button } from '@ui/uikit/components/common/Button';
import { useDropdownMenu } from '@ui/uikit/components/common/DropdownMenu';
import type {
  MessageMapping} from '@ui/uikit/components/common/SharingDropdownMenu';
import {
  SharingDropdownMenuController,
} from '@ui/uikit/components/common/SharingDropdownMenu';
import { Share } from '@ui/uikit/components/icons/mono/Share';

import { type FeedbackType } from './ApplicationUserFeedbackCarousel';

interface ReviewSharingControllerProps {
  applicationSlug: string;
  appName: string;
  feedbackType: FeedbackType;
  reviewId: number;
}

const MESSAGES_BY_PLATFORM: MessageMapping = {
  X: "Look what I've found on the #MagicStore, the World&apos;s #Web3 App Store! 🤩 Check this {{feedbackType}} for {{appName}} here: {{url}}",
  default:
    'Look at this {{feedbackType}} of {{appName}} on Magic Store! 📝\n' +
    'Check it out here: {{url}}',
};

const FALLBACK_SHARING_URL = typeof window != 'undefined' ? window.document.URL : undefined;

const ReviewSharingController = ({
  applicationSlug,
  appName,
  feedbackType,
  reviewId,
  children,
}: PropsWithChildren<ReviewSharingControllerProps>) => {
  const url = nextProjectUrl.store(`/projects/${applicationSlug}/${feedbackType.toLowerCase()}s/${reviewId}`, true);

  return (
    <SharingDropdownMenuController
      url={url || FALLBACK_SHARING_URL}
      platforms={['X', 'Discord', 'Telegram', 'Facebook', 'Reddit', 'LinkedIn', 'Email']}
      title="Share with friends"
      messageTemplate={MESSAGES_BY_PLATFORM}
      appName={appName}
      feedbackType={feedbackType}
    >
      {children}
    </SharingDropdownMenuController>
  );
};

interface ReviewSharingControlProps {
  applicationName?: string;
  author?: string;
  feedbackType: FeedbackType;
}

const ReviewSharingControl = ({ applicationName, author, feedbackType }: ReviewSharingControlProps) => {
  const { openMenu } = useDropdownMenu();

  const { user } = useAuth();

  const { open: openAuthModal } = useAuthModal();

  const button = useRef<HTMLButtonElement & HTMLAnchorElement>(null);

  const handleClick = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation();

      if (!flow.current) {
        console.warn(`Flow handle not initialized.`);

        return;
      }

      if (!button.current) {
        console.warn(`Button handle not initialized.`);

        return;
      }

      await flow.current.initiate();

      openMenu(button.current);
    },
    [openMenu],
  );

  const flow = useRef<ActivityHandle>(null);

  const signIn = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  return (
    <Fragment>
      <Activity ref={flow}>
        <Activity.Step requirement={!!user} action={signIn} key="signin" />
      </Activity>
      <Button
        size="small"
        variant="secondary"
        subVariant="ghost"
        onClick={handleClick}
        ref={button}
        label={ `Share ${ feedbackType.toLowerCase() } on ${ applicationName ?? 'application' } by ${ author } with your friends on social media` }
      >
        <Share />
      </Button>
    </Fragment>
  );
};

export { ReviewSharingControl, ReviewSharingController };
